import _ from 'lodash';
import parse from 'parse-json';
import { II18N } from '../localization';
import * as annotationTypes from './types';
import { markerGroups } from './markerGroups';

export const defaultSettings = {
    formats: {
        markerId: 'ann-{id}-ref',
        marker: '<a id="{markerId}" href="#{textId}">{marker}</a>',
        markerGroup: '<sup>{items}</sup>',
        markerSeparator: ',',
        textId: 'ann-{id}',
        text:
            '<li id="{textId}">' +
            '<sup><a href="#{markerId}">{marker}</a></sup>&nbsp;{text}' +
            '</li>',
        textGroup: '<ol>{items}</ol>',
        textSeparator: '',
        screenreaderTextGroup: '<span alt="({items})">&#8203;</span>',
        screenreaderTextGroupHeading: 'Annotations: ',
        screenreaderTextSeparator: ' ',
    },
};

type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>;
};

export class Annotator {
    updates: annotationTypes.AnnotationStateUpdate[];

    tempState: annotationTypes.AnnotationsStateShape;

    constructor(state?: RecursivePartial<annotationTypes.AnnotationsStateShape>) {
        if (typeof state === 'undefined' || !_.isArray(state?.items)) {
            throw new Error('Annotation array expected as first parameter');
        }

        // clone the state so it becomes mutable
        this.tempState = {
            items: [],
            annotationsIds: [],
            deduplicate: state.deduplicate,
            formats:
                typeof state.formats === 'object' && state.formats !== null
                    ? Object.entries(state.formats || {})
                          .filter(([_k, v]) => typeof v === 'string' && v.length > 0)
                          .reduce((memo, [k, v]) => ({ ...memo, [k]: v }), {})
                    : {},
            markerCount: state.markerCount || 0,
            markerGroupCount:
                typeof state?.markerGroupCount === 'object' && state.markerGroupCount != null
                    ? Object.entries(state.markerGroupCount)
                          .filter(([_k, v]) => typeof v === 'string')
                          .reduce(
                              (memo, [k, v]) => ({ ...memo, [k]: { count: v?.count || 0 } }),
                              {}
                          )
                    : {},
            defaultMarkerGroup: state?.defaultMarkerGroup,
        };

        if (Array.isArray(state?.items)) {
            const annotations = state.items as annotationTypes.Annotation[];
            Annotator.addAnnotations(annotations, this.tempState);
        }

        this.updates = [];
    }

    static addAnnotations(
        annotations: annotationTypes.Annotation[],
        dest: annotationTypes.AnnotationsStateShape
    ) {
        for (const annotation of annotations) {
            if (
                typeof annotation !== 'undefined' &&
                annotation !== null &&
                typeof annotation.id === 'string'
            ) {
                // this is all the deduplication logic we have right now :(
                const existingAnnotation = dest.items.find(
                    (ann) => ann.id === annotation.id || ann.text === annotation.text
                );

                const duplicate =
                    existingAnnotation && dest.deduplicate !== false
                        ? existingAnnotation.id
                        : annotation.id;
                dest.items.push({
                    id: annotation.id,
                    _id: annotation._id,
                    duplicate: duplicate,
                    markerGroup: annotation.markerGroup,
                    inUse: annotation.inUse,
                    marker: annotation.marker,
                    markerOrder: annotation.markerOrder,
                    markerTargetId: annotation.markerTargetId,
                    sortOrder: annotation.sortOrder,
                    text: annotation.text,
                    textTargetId: annotation.textTargetId,
                });
            }
        }

        return dest;
    }

    static updateState(
        u: annotationTypes.AnnotationStateUpdate,
        dest: annotationTypes.AnnotationsStateShape
    ) {
        if (u.markerGroup) {
            let markerGroup = dest.markerGroupCount[u.markerGroup];

            if (!markerGroup) {
                dest.markerGroupCount[u.markerGroup] = { count: 0 };
                markerGroup = dest.markerGroupCount[u.markerGroup];
            }

            if (u.markerGroupCount) {
                markerGroup.count = u.markerGroupCount;
            }
        }

        const ann = dest.items.find((a) => a.id === u.id);

        if (ann) {
            if (u.marker) {
                ann.marker = u.marker;
            }

            if (u.markerOrder) {
                ann.markerOrder = u.markerOrder;
            }

            if (u.duplicate) {
                ann.duplicate = u.duplicate;
            }

            ann.inUse = true;
        }

        if (u.markerCount) {
            dest.markerCount = u.markerCount;
        }

        return dest;
    }

    private update(u: annotationTypes.AnnotationStateUpdate) {
        this.updates.push(u);
        Annotator.updateState(u, this.tempState);
    }

    /**
     * Returns a copy of the given array of annotations, deduplicated and
     * sorted, and assigns markers to those annotations.
     * @private
     * @param {array<object>} arr
     * @returns {array<object>}
     */
    private processSelectedAnnotations(
        arr: annotationTypes.Annotation[],
        options: {
            assignMarker: boolean;
        } = { assignMarker: true }
    ) {
        // This is called every time a set of annotations is fetched for usage,
        // to deduplicate and sort annotations.
        const allAnnotations = this.tempState.items;

        // Keep index of annotations we've added for fast lookup.
        const addedAnnotationIds: { [k: string]: boolean } = {};

        // Deduplicate selection.
        const deduplicatedAnns = _.reduce(
            arr,
            (memo: annotationTypes.Annotation[], ann: annotationTypes.Annotation) => {
                if (ann.duplicate && ann.duplicate !== ann.id) {
                    // This is a duplicate annotation (an alias)...

                    if (!addedAnnotationIds[ann.duplicate]) {
                        // ...and the original annotation hasn't been
                        // added to the final result set yet.
                        const parentAnn = _.find(
                            allAnnotations,
                            (o: annotationTypes.Annotation) => o.id === ann.duplicate
                        );

                        // This if-statement is a redundant safety check...
                        // Guaranteed to have non-null `parentAnn` unless the user
                        // messed with our internal data collection.
                        if (parentAnn) {
                            memo.push(parentAnn);
                            addedAnnotationIds[parentAnn.id] = true;
                        }
                    }
                } else if (!addedAnnotationIds[ann.id]) {
                    // This is the first time we've seen this annotation, so we
                    // can add it to the final list.

                    memo.push(ann);
                    addedAnnotationIds[ann.id] = true;
                }

                return memo;
            },
            []
        );

        // Assign markers.
        if (options.assignMarker) {
            _.each(deduplicatedAnns, (ann: annotationTypes.Annotation) => this.assignMarker(ann));
        }

        // Sort, because marker assignment updates marker order.
        const sorted = this.sortSelectedAnnotations(deduplicatedAnns);
        return sorted;
    }

    /**
     * Sorts the given array of annotations, so that it's ordered by marker
     * group, and then by marker order within each group. Annotations with
     * custom markers (and don't belong to any group) receive the group sort
     * order defined in `settings.undefinedGroupSortOrder`.
     * @param {array<object>} arr
     * @param {array<object>} - Sorted copy of `arr`.
     */
    private sortSelectedAnnotations(arr: annotationTypes.Annotation[]) {
        return (
            _.chain(arr)
                // Sort by marker order
                .sortBy((ann: annotationTypes.Annotation) => ann.markerOrder)
                .sortBy((ann: annotationTypes.Annotation) => ann.markerGroup || '')
                .value()
        );
    }

    /**
     * Assigns a marker to the given annotation. Additionally:
     *  - Marks it as used (via `inUse`)
     *  - Gives it a marker order (via `markerOrderProperty`)
     * @private
     * @param {object} ann
     */
    private assignMarker(ann: annotationTypes.Annotation) {
        // If it doesn't have a marker yet, assign one
        let marker: string | undefined;
        let markerGroup: string | undefined;
        let markerGroupCount: number | undefined;

        // assign a marker to this annotation if it doesn't have one
        if (!ann.marker) {
            markerGroup = ann.markerGroup || this.tempState.defaultMarkerGroup;

            if (!markerGroup) {
                markerGroup = 'Numeric';
            }

            if (!this.tempState.markerGroupCount) throw new Error('markerGroups is undefined');

            const count = this.tempState.markerGroupCount[markerGroup]?.count || 0;
            markerGroupCount = count + 1;
            const mg = markerGroups[markerGroup];
            marker = mg(markerGroupCount);
            let markerCount = this.tempState.markerCount;
            let markerOrder = ann.markerOrder;

            if (!markerOrder) {
                markerOrder = ++markerCount;
            }

            this.update({
                id: ann.id,
                marker: marker,
                markerOrder,
                markerCount,
                markerGroup,
                markerGroupCount,
                duplicate: ann.duplicate,
            });
        } else if (!ann.markerOrder) {
            this.update({
                id: ann.id,
                markerOrder: this.tempState.markerCount + 1,
                markerCount: this.tempState.markerCount + 1,
            });
        }
    }

    /**
     * Returns annotations matching the given filters, deduplicated.
     * @param {(string|array<string>)} ids
     * @param {string} property
     * @returns {array<object>}
     */
    getAnnotations(ids: string | string[], property: keyof annotationTypes.Annotation) {
        const idArray = _.isArray(ids) ? ids : [ids];
        const filteredAnns = _.filter<annotationTypes.Annotation>(
            this.tempState.items,
            (ann: annotationTypes.Annotation) => idArray.indexOf(ann[property] as string) > -1
        );

        return this.processSelectedAnnotations(filteredAnns);
    }

    /**
     * Returns annotations matching the given filter object, deduplicated.
     * @param {object} filterObj
     * @returns {array<object>}
     */
    getAnnotationsWhere(
        filterObj: { [key: string]: any },
        options: { assignMarker: boolean } = { assignMarker: true }
    ) {
        if (filterObj.__inUse) {
            // this is only to ensure this change does not break client pages
            // that reference the old __inUse property directly.
            // once (and if) the usage is removed we can remove this code.
            filterObj.inUse = filterObj.__inUse;
            delete filterObj.__inUse;
        }

        const filteredAnns: annotationTypes.Annotation[] = _.filter<annotationTypes.Annotation>(
            this.tempState.items,
            filterObj
        );

        return this.processSelectedAnnotations(filteredAnns, options);
    }

    /**
     * Returns markers for the given array of annotations, as one HTML string.
     * @param {array<object>} arr
     * @returns {string}
     */
    private getFormattedMarkers(arr: annotationTypes.Annotation[]): string {
        const that = this;
        const formats = {
            ...defaultSettings.formats,
            ...this.tempState.formats,
        };
        const markerFormat = formats.marker;
        const markerText = arr
            .map((ann) => that.templateAnnotation(markerFormat, ann))
            .join(formats.markerSeparator);

        return formats.markerGroup.replace('{items}', markerText);
    }

    /**
     * Returns texts for the given array of annotations, as one HTML string.
     * @param {array<object>} arr
     * @returns {string}
     */
    private getFormattedTexts(arr: annotationTypes.Annotation[]): string {
        const formats = {
            ...defaultSettings.formats,
            ...this.tempState.formats,
        };
        const textFormat = formats.text;
        const itemText = arr
            .map((ann) => this.templateAnnotation(textFormat, ann))
            .join(formats.textSeparator);

        return formats.textGroup.replace('{items}', () => itemText);
    }

    /**
     * Return texts from the given array of annotations, formatted for
     * screenreader-only output, as one HTML string.
     * @param {array<object>} arr
     * @returns {string}
     */
    private getFormattedTextsForScreenreader(arr: annotationTypes.Annotation[]): string {
        // If there are no annotations, don't generate unnecessary HTML!
        // Return an empty string.
        if (arr.length < 1) return '';

        const formats = {
            ...defaultSettings.formats,
            ...this.tempState.formats,
        };
        const textsArr = arr.map((ann) => ann.text);
        let texts = textsArr.join(formats.screenreaderTextSeparator);

        if (textsArr.length > 1) {
            // When there are multiple annotations, include a heading before
            // the list of annotations.
            texts = `${formats.screenreaderTextGroupHeading}${texts}`;
        }

        return formats.screenreaderTextGroup.replace('{items}', () => texts);
    }

    /**
     * Returns `formatString` with the given annotation's data templated
     * into it.
     * @param {string} formatString
     * @param {object} ann
     * @returns {string}
     */
    private templateAnnotation(formatString: string, ann: annotationTypes.Annotation): string {
        const formats = {
            ...defaultSettings.formats,
            ...this.tempState.formats,
        };

        // Provide a replace function instead of directly replacing with the
        // string (e.g. `s.replace(regex, ann[props.id])`), to avoid default
        // behaviour of handling replace patterns that comes with a string
        // replacement. It will see strings like "$&" and treat that as a
        // placeholder to insert something, which is NOT what we want here.
        let s = formatString
            .replace(/\{id\}/g, () => ann.id)
            .replace(/\{marker\}/g, () => ann.marker || '')
            .replace(/\{text\}/g, () => ann.text || '');

        if (/\{markerId\}/g.test(s)) {
            s = s.replace(/\{markerId\}/g, () => this.templateAnnotation(formats.markerId, ann));
        }

        if (/\{textId\}/g.test(s)) {
            s = s.replace(/\{textId\}/g, () => this.templateAnnotation(formats.textId, ann));
        }

        return s;
    }

    /**
     * Returns all annotations, with markers assigned.
     * @returns {object[]}
     */
    all(): annotationTypes.Annotation[] {
        return this.processSelectedAnnotations(this.tempState.items);
    }

    /**
     * Returns all annotations, with markers assigned, in one formatted HTML
     * string.
     * @returns {string}
     */
    allTexts(): string {
        const processedAnns = this.processSelectedAnnotations(this.tempState.items);
        return this.getFormattedTexts(processedAnns);
    }

    /**
     * Returns texts for all used annotations, in one HTML formatted string.
     * @returns {string}
     */
    allUsedTexts(): string {
        const processedAnns = this.getAnnotationsWhere({
            inUse: true,
        });

        return this.getFormattedTexts(processedAnns);
    }

    /**
     * Returns markers for annotations matching the given ids, in one HTML
     * formatted string.
     * @param {string|string[]} ids
     * @returns {string}
     */
    markersById(ids: string | string[]): string {
        const processedAnns = this.getAnnotations(ids, 'id');
        return this.getFormattedMarkers(processedAnns);
    }

    /**
     * Returns markers for annotations with the given marker target ids, in
     * one HTML formatted string.
     * @param {string|string[]} ids
     * @returns {string}
     */
    markersByMarkerTargetId(ids: string | string[]): string {
        const processedAnns = this.getAnnotations(ids, 'markerTargetId');
        return this.getFormattedMarkers(processedAnns);
    }

    /**
     * Returns markers for annotations matching the given filter, in one HTML
     * formatted string.
     * @param {object} filterObj
     * @returns {string}
     */
    markersWhere(filterObj: { [key: string]: any }): string {
        const processedAnns = this.getAnnotationsWhere(filterObj);
        return this.getFormattedMarkers(processedAnns);
    }

    /**
     * Returns texts for annotations matching the given ids, in one HTML
     * formatted string.
     * @param {string|string[]} ids
     * @returns {string}
     */
    textsById(ids: string | string[]): string {
        const processedAnns = this.getAnnotations(ids, 'id');
        return this.getFormattedTexts(processedAnns);
    }

    /**
     * Returns texts for annotations with the given text target ids, in
     * one HTML formatted string.
     * @param {string|string[]} ids
     * @returns {string}
     */
    textsByTextTargetId(ids: string | string[]): string {
        const processedAnns = this.getAnnotations(ids, 'textTargetId');
        return this.getFormattedTexts(processedAnns);
    }

    /**
     * Returns texts for annotations matching the given filter, in one HTML
     * formatted string.
     * @param {object} filterObj
     * @returns {string}
     */

    textsWhere(
        filterObj: { [key: string]: any },
        options: { assignMarker: boolean } = { assignMarker: false }
    ): string {
        const processedAnns = this.getAnnotationsWhere({ ...filterObj }, options);
        return this.getFormattedTexts(processedAnns);
    }

    /**
     * Returns texts for annotations matching the given ids, formatted for
     * screenreader-only output, as one HTML string.
     * @param {string|string[]} ids
     * @returns {string}
     */
    srTextsById(ids: string | string[]): string {
        const processedAnns = this.getAnnotations(ids, 'id');
        return this.getFormattedTextsForScreenreader(processedAnns);
    }

    /**
     * Returns texts for annotations with the given text target ids, formatted
     * for screenreader-only output, as one HTML string.
     * @param {string|string[]} ids
     * @returns {string}
     */
    srTextsByTextTargetId(ids: string | string[]): string {
        const processedAnns = this.getAnnotations(ids, 'textTargetId');
        return this.getFormattedTextsForScreenreader(processedAnns);
    }

    /**
     * Returns texts for annotations matching the given filter, formatted for
     * screenreader-only output, as one HTML string.
     * @param filterObj
     * @returns {string}
     */
    srTextsWhere(filterObj: { [key: string]: any }): string {
        const processedAnns = this.getAnnotationsWhere(filterObj);
        return this.getFormattedTextsForScreenreader(processedAnns);
    }

    getAnnotationsX = (
        filterBy: string | undefined,
        filters: annotationTypes.AnnotationFilters,
        data: Record<string, unknown> | undefined,
        i18n: II18N
    ) => {
        let filterObject;

        if (typeof filterBy === 'object') {
            filterObject = filterBy;
        } else {
            try {
                filterObject = parse((filterBy as string) || '{}');
            } catch (error) {
                filterObject = {};
            }
        }

        let annotations: any = '';

        try {
            annotations = this.textsWhere({ ...filterObject, ...filters });
        } catch (e) {
            console.log(e);
        }

        return i18n.format(annotations || '', data) || '';
    };
}
